import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';

import { DataRow, useTableViewState } from '~components/table-view';
import EditableTableModal from '~support-at-home/components/common/EditableTableModal';

import { ClientService } from '~support-at-home/models';

import columnsFactory from '~support-at-home/forms/client-services/listSingle';

import { useGetSahServicesQuery } from '~lookups/actions/sahServices';
import { create, deleteOne, getAll, update } from '~support-at-home/actions/clientServices';

import { selectSahCategories } from '~lookups/selectors/sahCategories';
import { selectSahClientServices } from '~support-at-home/selectors/clientServices';

import { filterRowFields } from '../helpers';

interface ComponentProps {
  id: number | null;
  isOpen?: boolean;
  onClose: (confirm: boolean) => void;
}

const ClientServicesModal: React.FC<ComponentProps> = ({ id, isOpen = false, onClose }) => {
  const dispatch = useAppDispatch();
  const clientServices = useAppSelector(selectSahClientServices);
  const categories = useAppSelector(selectSahCategories);
  const { data: servicesData } = useGetSahServicesQuery();
  const { data, setData } = useTableViewState<DataRow<ClientService>>(clientServices as DataRow<ClientService>[]);
  const combinedData = data;

  const columns = columnsFactory(servicesData ?? [], categories);
  const getDefaultRow = () => {
    // Ensure required fields are present
    return {
      id: crypto.randomUUID(),
      client_id: id,
      service_id: '',
      start_date: '',
      end_date: '',
    } as DataRow<ClientService>;
  };

  const load = async () => {
    dispatch(getAll({ client_id: id }));
  };

  useEffect(() => {
    if (isOpen && id !== null) {
      load();
    }
  }, [id, isOpen]);

  useEffect(() => {
    const persistedServices = clientServices.map((service) => ({
      ...service,
      persisted: true,
      backendId: service.id,
    })) as DataRow<ClientService>[];

    setData(persistedServices);
  }, [clientServices]);
  return (
    <EditableTableModal
      isOpen={isOpen}
      title="Allocated SAH Services"
      data={combinedData}
      columns={columns}
      onClose={(shouldReload) => {
        onClose(shouldReload);
        if (shouldReload) load();
      }}
      onCreate={async (row) => {
        const toCreate = filterRowFields(row, columns, id);
        await dispatch(create(toCreate)).unwrap();
        await load();
      }}
      onUpdate={async (row) => {
        if (!row.backendId) {
          return;
        }
        const toUpdate = filterRowFields(row, columns, id);
        await dispatch(update({ ...toUpdate, id: row.backendId })).unwrap();
        await load();
      }}
      onDelete={async (row) => {
        if (!row.backendId) {
          return;
        }
        try {
          await dispatch(deleteOne({ id: row.backendId })).unwrap();
          await load();
        } catch (error) {
          console.error('Error deleting service:', error);
        }
      }}
      getDefaultRow={getDefaultRow}
      allowEdit={true}
      allowAdd={true}
      allowDelete={true}
    />
  );
};

export default ClientServicesModal;
